import React, { memo, useCallback, useMemo } from "react";
import { Grid, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import cn from "classnames";
import { ListItemProps, ListOptionBase } from "@shared/components/List/models";

const useStyles = makeStyles((theme) => ({
  root: {
    cursor: "pointer",
    lineHeight: 1,
    transition: "all ease-in .15s",
    backgroundColor: theme.palette.common.white,
    "&:hover": {
      backgroundColor: "rgba(208, 232, 248, 0.3)",
    },
  },
  label: {
    whiteSpace: "normal", // Allow text to wrap
    wordBreak: "break-word",
  },
  small: {
    padding: theme.spacing(2.5, 5),
    fontSize: 14,
  },
  medium: {
    padding: theme.spacing(3.5, 5),
    fontSize: 16,
  },
  large: {
    padding: theme.spacing(4.5, 5),
    fontSize: 18,
  },
  selected: {
    backgroundColor: theme.palette.info.light,
  },
  disabled: {
    color: theme.palette.grey[500],
    cursor: "default",
    "&:hover": {
      backgroundColor: theme.palette.common.white,
    },
  },
}));

const SelfServiceListItem = <ListOption extends ListOptionBase>({
  option,
  size,
  style,
  selected,
  onOptionClick,
}: ListItemProps<ListOption>) => {
  const classes = useStyles();

  // Always show tooltip for all options to ensure consistency
  const tooltipTitle = option.tooltip ?? option.label;

  const className = useMemo(
    () =>
      cn({
        [classes.root]: true,
        [classes.small]: size === "small",
        [classes.medium]: size === "medium",
        [classes.large]: size === "large",
        [classes.selected]: selected,
        [classes.disabled]: option.disabled,
      }),
    [classes, selected, size, option.disabled]
  );

  const handleClick = useCallback(() => {
    if (option.disabled) return;

    onOptionClick(option);
  }, [option, onOptionClick]);

  const testIdFromLabel = option.label.toLowerCase().replaceAll(" ", "-");

  return (
    <Tooltip disableInteractive title={tooltipTitle}>
      <Grid
        container
        style={style}
        className={className}
        onClick={handleClick}
        data-testid={`list-option-${testIdFromLabel}`}
      >
        {option.startAdornment && (
          <Grid item xs={1} marginRight={2}>
            {option.startAdornment}
          </Grid>
        )}
        <Grid item xs className={classes.label} data-testid={`list-option-text-${testIdFromLabel}`}>
          {option.label}
        </Grid>
        {option.endAdornment && (
          <Grid item xs={1} marginLeft={2}>
            {option.endAdornment}
          </Grid>
        )}
      </Grid>
    </Tooltip>
  );
};

export default memo(SelfServiceListItem) as typeof SelfServiceListItem;
